import type { AlpineComponent } from 'alpinejs'

const debounce = (
  func: (...args: any[]) => void,
  wait: number,
  immediate = true,
) => {
  let timeout: ReturnType<typeof setTimeout> | undefined
  return (...args: any[]) => {
    const context = this
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(function () {
      timeout = undefined
      if (!immediate) {
        func.apply(context, args)
      }
    }, wait)
    if (callNow) func.apply(context, args)
  }
}

/**
 * Append the child element and wait for the parent's
 * dimensions to be recalculated
 */
const appendChildAwaitLayout = (parent: HTMLElement, element: HTMLElement) => {
  return new Promise((resolve, _) => {
    const resizeObserver = new ResizeObserver((entries, observer) => {
      observer.disconnect()
      resolve(entries)
    })
    resizeObserver.observe(element)
    parent.appendChild(element)
  })
}

export function Marquee(): AlpineComponent<{
  speed: number
  spaceX: number
  dynamicWidthElements: boolean
  originalElement: Node | null
  init(): void
  resize(): void
  discard(): void
}> {
  return {
    speed: 1,
    spaceX: 0,
    dynamicWidthElements: false,
    originalElement: null,

    async init() {
      if (this.dynamicWidthElements) {
        const images = this.$el.querySelectorAll('img')
        // If there are any images, make sure they are loaded before
        // we start cloning them, since their width might be dynamically
        // calculated
        if (images) {
          await Promise.all(
            Array.from(images).map((image) => {
              return new Promise((resolve, _) => {
                if (image.complete) {
                  resolve(void 0)
                } else {
                  image.addEventListener('load', () => {
                    resolve(void 0)
                  })
                }
              })
            }),
          )
        }
      }

      // Store the original element so we can restore it on screen resize later
      this.originalElement = this.$el.cloneNode(true)
      const originalWidth = this.$el.scrollWidth + this.spaceX * 4
      // Required for the marquee scroll animation
      // to loop smoothly without jumping
      this.$el.style.setProperty('--marquee-width', originalWidth + 'px')
      this.$el.style.setProperty(
        '--marquee-time',
        ((1 / this.speed) * originalWidth) / 100 + 's',
      )
      this.resize()
      // Make sure the resize function can only be called once every 100ms
      // Not strictly necessary but stops lag when resizing window a bit
      window.addEventListener('resize', debounce(this.resize.bind(this), 100))
    },

    async resize() {
      // Reset to original number of elements
      this.$el.innerHTML = this.originalElement?.innerHTML || ''

      // Keep cloning elements until marquee starts to overflow
      let i = 0

      while (this.$el.scrollWidth <= this.$el.clientWidth) {
        if (this.dynamicWidthElements) {
          // If we don't give this.$el time to recalculate its dimensions
          // when adding child nodes, the scrollWidth and clientWidth won't
          // change, thus resulting in this while loop looping forever
          await appendChildAwaitLayout(
            this.$el,
            this.originalElement?.children[i].cloneNode(true) || null,
          )
        } else {
          this.$el.appendChild(
            this.originalElement?.children[i].cloneNode(true) || null,
          )
        }
        i += 1
        i = i % (this.originalElement?.childElementCount || 0)
      }

      // Add another (original element count) of clones so the animation
      // has enough elements off-screen to scroll into view
      let j = 0
      while (j < (this.originalElement?.childElementCount || 0)) {
        this.$el.appendChild(
          this.originalElement?.children[i].cloneNode(true) || null,
        )
        j += 1
        i += 1
        i = i % (this.originalElement?.childElementCount || 0)
      }
    },

    discard() {},
  }
}
